import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import SiteService, { type Jurisdiction, type Site } from '@frontend/AppSettings/services/SiteService';
import { useBidCapabilities } from '@frontend/BidSheets/useBidCapabilities';

/**
 * The Sites tab: where a project's work physically happens, which is what picks the law.
 *
 * P6 is build-beside — nothing classifies hours yet — so this screen records data that only P7
 * consumes. It lands a phase early on purpose: P7 requires a site on every matter it classifies,
 * and a picker with nothing to pick is a migration problem rather than a UI one.
 *
 * TWO FIELDS CARRY THE WHOLE DESIGN, and they are easy to mistake for one:
 *
 *  - JURISDICTION is the law. It decides thresholds, multipliers and averaging ceilings, and it is
 *    system-owned reference data (there is no write path to it anywhere in the app).
 *  - TIMEZONE is which local DAY an hour fell in. It defaults from the jurisdiction and is
 *    overridable here, because the boundary that needs overriding is geographic rather than
 *    provincial: British Columbia's Peace River region holds Mountain Standard year-round while
 *    the rest of the province moves, and Saskatchewan never moves at all.
 *
 * The default is applied as a CONVENIENCE, not a guarantee — the server resolves the same fallback
 * itself (`payload.timezone or jurisdiction.default_timezone`) and validates the result against
 * the IANA database, so a client that skips this form cannot store a zone this one would refuse.
 *
 * WRITE-CONTROL GATING, AND WHERE IT DOES NOT LINE UP WITH THE SERVER. The server gates every
 * mutation on permish {1} (system administration). The nearest thing this app has client-side is
 * `useBidCapabilities().isBidAdmin`, which is ADMIN_CATALOGS — held by a bid administrator OR by a
 * permish-1 caller through the retained superuser fallback. The two sets overlap but are not
 * equal, so the affordance is wrong in both directions and neither is a security hole:
 *
 *   - a bid administrator WITHOUT permish 1 sees the form and gets a 403 on submit;
 *   - on a tenant with `bids` disabled, `useBidCapabilities` fails closed to no capabilities, so a
 *     permish-1 admin the server WOULD accept sees no controls at all. That is the legal-line case
 *     `site_endpoints.py` cites as its reason for choosing permish {1} in the first place.
 *
 * Left as-is rather than papered over: there is no permish-bearing client-side hook to read, and
 * inventing one is its own piece of work. Recorded in the phase file as a follow-up. Hiding
 * controls was never the boundary — the server is.
 */
interface ProjectSitesTabProps {
  matterid: string;
}

/**
 * The zone list, from the platform rather than a constant.
 *
 * `Intl.supportedValuesOf` is the browser's own IANA database, which is the same source the server
 * validates against — a hardcoded list would drift from it and would also have to be edited every
 * time a jurisdiction is added, which is the failure the whole phase is built to avoid. The
 * fallback covers the seeded provinces plus Dawson Creek for older engines.
 */
const FALLBACK_ZONES = [
  'America/Edmonton',
  'America/Regina',
  'America/Vancouver',
  'America/Dawson_Creek',
  'America/Toronto',
  'America/Winnipeg',
  'America/Halifax',
  'America/St_Johns',
];

const zoneOptions = (mustInclude: string): string[] => {
  const intl = Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] };
  let zones: string[];
  try {
    zones = intl.supportedValuesOf
      ? intl.supportedValuesOf('timeZone').filter((z) => z.startsWith('America/'))
      : FALLBACK_ZONES;
  } catch {
    zones = FALLBACK_ZONES;
  }
  // A jurisdiction's own default must always be selectable even if the engine does not list it,
  // or the form could not express the value the server would have chosen by itself.
  return zones.includes(mustInclude) ? zones : [mustInclude, ...zones];
};

const ProjectSitesTab = ({ matterid }: ProjectSitesTabProps): JSX.Element => {
  const queryClient = useQueryClient();
  const capabilities = useBidCapabilities();
  const mayManage = capabilities?.isBidAdmin ?? false;

  const [name, setName] = useState('');
  const [jurisdictionId, setJurisdictionId] = useState('');
  const [timezone, setTimezone] = useState('');
  const [error, setError] = useState<string | null>(null);

  const sites = useQuery({
    queryKey: ['ProjectSites', matterid],
    queryFn: () => SiteService.getSitesForMatter(matterid),
  });

  // The catalog every site references. Fetched for readers too, because the table renders a
  // jurisdiction NAME and an id would be useless to the person reading it.
  //
  // INACTIVE ONES INCLUDED, because reading and picking are different questions. A site keeps
  // pointing at its jurisdiction after that jurisdiction is retired, so an active-only map left
  // such a site rendering "—" for its province and silently dropped the "overrides …" annotation
  // that makes a Peace River zone legible — the row would look like an ordinary site.
  const jurisdictions = useQuery({
    queryKey: ['Jurisdictions', 'includeInactive'],
    queryFn: () => SiteService.getJurisdictions(true),
  });

  const jurisdictionById = useMemo(() => {
    const map = new Map<string, Jurisdiction>();
    (jurisdictions.data ?? []).forEach((j) => map.set(j.id, j));
    return map;
  }, [jurisdictions.data]);

  // ...but only a LIVE jurisdiction may be chosen for a new site: the server refuses an inactive
  // one ("that jurisdiction does not exist or is not active"), so offering it would be a form that
  // 400s on submit.
  const selectableJurisdictions = useMemo(
    () => (jurisdictions.data ?? []).filter((j) => j.is_active),
    [jurisdictions.data],
  );

  // Hoisted out of the JSX, where it re-ran `Intl.supportedValuesOf` and a filter over the whole
  // IANA set on every keystroke in the name field — the list depends only on the selected
  // jurisdiction's default zone.
  const selectedZoneOptions = useMemo(
    () =>
      jurisdictionId
        ? zoneOptions(jurisdictionById.get(jurisdictionId)?.default_timezone ?? timezone)
        : [],
    [jurisdictionId, jurisdictionById, timezone],
  );

  /**
   * D13's layer three: picking a jurisdiction DEFAULTS the zone to that province's, and the field
   * stays editable so a Peace River site can override it.
   *
   * Done in the change handler rather than in an effect on `[jurisdictionId, jurisdictionById]`,
   * which is what this was first written as and is a real bug: `jurisdictionById` is memoised on
   * the query's data, so ANY refetch — a window focus, an invalidation after adding a site — mints
   * a new Map identity, re-runs the effect and resets the zone to the province default. An admin
   * who had just selected America/Dawson_Creek would silently get America/Vancouver back, and the
   * form would look like they had chosen it.
   */
  const pickJurisdiction = (id: string) => {
    setJurisdictionId(id);
    setTimezone(jurisdictionById.get(id)?.default_timezone ?? '');
  };

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['ProjectSites', matterid] });

  const addSite = useMutation({
    mutationFn: () =>
      SiteService.createSite({
        matterid,
        name: name.trim(),
        jurisdiction_id: jurisdictionId,
        timezone: timezone || undefined,
      }),
    onSuccess: () => {
      setName('');
      setError(null);
      void refresh();
    },
    // The server's refusal verbatim — an unknown zone, a duplicate name, a matter that is not
    // this tenant's. Replacing it with a generic line would hide which of those happened.
    onError: (err: unknown) =>
      setError((err as Error)?.message || 'Could not add that site.'),
  });

  const setActive = useMutation({
    mutationFn: ({ site, active }: { site: Site; active: boolean }) =>
      active ? SiteService.reactivateSite(site.id) : SiteService.deactivateSite(site.id),
    onSuccess: () => {
      setError(null);
      void refresh();
    },
    onError: (err: unknown) =>
      setError((err as Error)?.message || 'Could not change that site.'),
  });

  const rows = sites.data ?? [];
  const canSubmit = name.trim().length > 0 && jurisdictionId.length > 0 && !addSite.isPending;

  return (
    <div className="pipeline-panel">
      <h3 className="pipeline-panel-title">Sites</h3>
      <p className="pipeline-panel-subtitle">
        Where this project&apos;s work happens. The jurisdiction sets which employment standards
        apply to hours booked here; the timezone decides which local day an hour falls in.
      </p>

      {sites.isLoading && <p className="pipeline-muted">Loading sites…</p>}
      {/* React Query keeps the last good data across a failed REFETCH, so "could not be loaded"
          above a populated table would be a lie about the table. */}
      {sites.isError && rows.length === 0 && (
        <p className="pipeline-muted">This project&apos;s sites could not be loaded.</p>
      )}
      {sites.isError && rows.length > 0 && (
        <p className="pipeline-muted">This list could not be refreshed — it may be out of date.</p>
      )}
      {sites.isSuccess && rows.length === 0 && (
        <p className="pipeline-muted">
          No sites recorded yet. Hours booked on this project cannot be classified against
          employment standards until it has one.
        </p>
      )}

      {rows.length > 0 && (
        <table className="pipeline-table">
          <thead>
            <tr>
              <th>Site</th>
              <th>Jurisdiction</th>
              <th>Timezone</th>
              <th>Status</th>
              {mayManage && <th aria-label="Actions" />}
            </tr>
          </thead>
          <tbody>
            {rows.map((site) => {
              const parent = jurisdictionById.get(site.jurisdiction_id);
              const inherited = parent?.default_timezone;
              return (
                <tr key={site.id}>
                  <td>{site.name}</td>
                  <td>{parent ? `${parent.name} (${parent.code})` : '—'}</td>
                  <td>
                    {site.timezone}
                    {/* Worth surfacing: an overridden zone is the Peace River case, and it is
                        invisible from the value alone unless you know the parent's. */}
                    {inherited && inherited !== site.timezone && (
                      <span className="pipeline-muted"> · overrides {inherited}</span>
                    )}
                  </td>
                  <td>{site.is_active ? 'Active' : 'Retired'}</td>
                  {mayManage && (
                    <td>
                      <button
                        type="button"
                        className="pipeline-secondary-btn"
                        // Only the row being changed goes busy — `isPending` alone would grey out
                        // every button on the project.
                        disabled={
                          setActive.isPending && setActive.variables?.site.id === site.id
                        }
                        onClick={() =>
                          setActive.mutate({ site, active: !site.is_active })
                        }
                      >
                        {site.is_active ? 'Retire' : 'Reactivate'}
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {error && (
        <p className="pipeline-form-error" role="alert">
          {error}
        </p>
      )}

      {mayManage && (
        <form
          className="pipeline-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) addSite.mutate();
          }}
        >
          <div className="pipeline-form-row">
            <label>
              Site name
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="North Yard"
              />
            </label>
            <label>
              Jurisdiction
              <select
                value={jurisdictionId}
                onChange={(e) => pickJurisdiction(e.target.value)}
                disabled={jurisdictions.isLoading || jurisdictions.isError}
              >
                <option value="">Select a jurisdiction…</option>
                {selectableJurisdictions.map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.name} ({j.code})
                  </option>
                ))}
              </select>
            </label>
            <label>
              Timezone
              <select
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                disabled={!jurisdictionId}
              >
                {selectedZoneOptions.map((zone) => (
                  <option key={zone} value={zone}>
                    {zone}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {jurisdictions.isError && (
            <p className="pipeline-muted">
              Jurisdictions could not be loaded, so a site cannot be added right now.
            </p>
          )}
          <div className="pipeline-form-actions">
            <button type="submit" className="pipeline-action-btn" disabled={!canSubmit}>
              {addSite.isPending ? 'Adding…' : 'Add site'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ProjectSitesTab;
